import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Building2, GraduationCap, Users, Briefcase, Check } from 'lucide-react';
import { IMAGES, BUSINESS_OFFERINGS } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Building2, GraduationCap, Users, Briefcase };

const ForBusiness = () => {
  const [form, setForm] = useState({ company: '', name: '', email: '', size: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/business-leads`, form);
      setSubmitted(true);
      setForm({ company: '', name: '', email: '', size: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setError('We could not submit your request. Please try again shortly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative bg-[#0f1113] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-70"
          style={{ backgroundImage: `url(${IMAGES.business})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1113] via-[#0f1113]/85 to-transparent" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-28">
          <div className="max-w-xl">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] mb-6 font-medium">FOR BUSINESS</p>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Concierge Wellness. <br />Corporate Scale.
            </h1>
            <p className="text-white/80 text-base leading-relaxed max-w-md">
              Elevate the health and readiness of your workforce with turn-key wellness, phlebotomy, and life-saving training programs delivered on your premises.
            </p>
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="bg-[#f5f0ea] py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-5">CORPORATE OFFERINGS</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1c1e20] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Programs Built Around Your Team.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BUSINESS_OFFERINGS.map((o) => {
              const Icon = ICONS[o.icon];
              return (
                <div key={o.title} className="bg-white border border-black/5 p-8 flex gap-5 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-full border border-[#c9a961] flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#c9a961]" strokeWidth={1.4} />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl text-[#1c1e20] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{o.title}</h3>
                    <p className="text-[#4a4a4a] text-[14px] leading-relaxed">{o.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why partner */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <img src={IMAGES.business} alt="Corporate" className="w-full h-[480px] object-cover" />
          <div>
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-5">WHY PARTNER WITH AURELIA</p>
            <h2 className="font-serif text-4xl text-[#1c1e20] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              A trusted extension of your team.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mb-6" />
            <ul className="space-y-4">
              {[
                'Fully licensed, insured, and HIPAA compliant clinical team',
                'Flexible on-site scheduling, including evenings and weekends',
                'Custom program design tailored to your workforce and goals',
                'Detailed reporting delivered with discretion and clarity',
                'Single point of contact for seamless coordination',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#3c3c3c] text-[15px]">
                  <Check size={18} className="text-[#c9a961] mt-0.5 flex-shrink-0" strokeWidth={2} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="bg-[#f5f0ea] py-20 md:py-24">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">REQUEST A PROPOSAL</p>
            <h2 className="font-serif text-4xl text-[#1c1e20] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Let’s build a program for your team.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto" />
          </div>
          {submitted ? (
            <div className="bg-white border border-[#c9a961] p-8 text-center">
              <p className="text-[#1c1e20] text-lg font-serif" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Thank you. A member of our team will reach out shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-black/5 p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company" className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]" />
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Contact name" className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email" className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]" />
              <input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="Team size" className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your program needs" rows="5" className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961] md:col-span-2" />
              {error && <p className="text-red-600 text-sm md:col-span-2">{error}</p>}
              <div className="md:col-span-2 flex justify-center">
                <button type="submit" disabled={submitting} className="bg-[#c9a961] hover:bg-[#b89751] transition-colors duration-300 text-[#141618] tracking-[0.22em] text-[12px] font-semibold px-10 py-4 disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? 'SUBMITTING…' : 'REQUEST PROPOSAL'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ForBusiness;
